import React, { useState } from 'react';
import {
  Button,
  TextField,
  Container,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Header';
import axios from 'axios';
import { toast } from "react-toastify";
import { Visibility, VisibilityOff } from '@mui/icons-material';
import ReCAPTCHA from "react-google-recaptcha";

function Signup() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formdata, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    password2: "",
  });
  const [captchaToken, setCaptchaToken] = useState(null); // State to store CAPTCHA token
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL;
  const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY;

  const handleOnChange = (e) => {
    setFormData({ ...formdata, [e.target.name]: e.target.value });
  };

  const handleCaptchaChange = (token) => {
    // console.log("reCAPTCHA Token:", value);
    setCaptchaToken(token); // Store the CAPTCHA token
  };

  const { first_name, last_name, email, password, password2 } = formdata;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // console.log("Form data:", { ...formdata, recaptcha_token: captchaToken });

    if (!first_name || !last_name || !email || !password || !password2) {
      // setError("All fields are required");
      toast.error("All fields are required");
    } else if (password !== password2) {
      // setError("Passwords do not match");
      toast.error("Passwords do not match");
    } else if (!captchaToken) {
      // setError("Please complete the CAPTCHA");
      toast.error("Please complete the reCAPTCHA verification.");
    } else {
      setIsLoading(true);
      try {
        const res = await axios.post(`${apiUrl}/api/v1/auth/register/`, {
          ...formdata,
          recaptcha_token: captchaToken,  // Ensure it matches the backend field name, // Include the CAPTCHA token in the request
        });
        setIsLoading(false);
        const response = res.data;

        if (res.status === 201) {
          navigate('/otp-verify');
          toast.success(response.message || "Registration successful. Please verify your email.");
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Signup error:", error);
        toast.error("Signup failed. Please try again.");

        let errorMessage = "Signup failed. Please try again.";
        if (error.response && error.response.data) {
          if (error.response.status === 400) {
            const errors = error.response.data;
            for (const key in errors) {
              if (errors.hasOwnProperty(key)) {
                toast.error(`${key}: ${errors[key][0]}`);
              }
            }
          } else {
            errorMessage = error.response.data.message || errorMessage;
            toast.error(errorMessage);
          }
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Header />
      <Box display="flex" justifyContent="center" alignItems="center" mt={{ xs: 2, md: 3 }} mb={{ xs: 1, md: 2 }}>
        <HomeIcon fontSize="large" />
        <Typography variant="h4" ml={1} fontSize={{ xs: '1.5rem', md: '2rem' }}>
          Signup
        </Typography>
      </Box>
      <Container maxWidth="sm">
        <Box sx={{ p: { xs: 2, md: 3 }, boxShadow: 2, borderRadius: 2 }}>
          <form onSubmit={handleSubmit}>
            <Typography color="error" variant="body2" align="center">
              {error ? error : ""}
            </Typography>
            <TextField
              label="First Name"
              name="first_name"
              value={first_name}
              onChange={handleOnChange}
              variant="outlined"
              margin="normal"
              fullWidth
              required
              InputLabelProps={{ style: { fontSize: '1rem' } }}
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={last_name}
              onChange={handleOnChange}
              variant="outlined"
              margin="normal"
              fullWidth
              required
              InputLabelProps={{ style: { fontSize: '1rem' } }}
            />
            <TextField
              label="Email"
              name="email"
              value={email}
              onChange={handleOnChange}
              variant="outlined"
              margin="normal"
              fullWidth
              required
              InputLabelProps={{ style: { fontSize: '1rem' } }}
            />
            <TextField
              label="Password"
              name="password"
              value={password}
              onChange={handleOnChange}
              type={showPassword ? "text" : "password"}
              variant="outlined"
              margin="normal"
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ style: { fontSize: '1rem' } }}
            />
            <TextField
              label="Confirm Password"
              name="password2"
              value={password2}
              onChange={handleOnChange}
              type={showConfirmPassword ? "text" : "password"}
              variant="outlined"
              margin="normal"
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              InputLabelProps={{ style: { fontSize: '1rem' } }}
            />
            <Box display="flex" justifyContent="center" mt={2}>
              <ReCAPTCHA
                sitekey={siteKey}
                // sitekey="6Lfhlp0qAAAAAKHLPZTSwFRKz3fvX_Qm5Ut2fyO2"
                // sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} // Use your reCAPTCHA site key
                onChange={handleCaptchaChange}
              />
            </Box>
            <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2, py: 1.5, fontSize: { xs: '0.9rem', md: '1rem' } }}>
              Signup
            </Button>
          </form>
          <Typography mt={2} textAlign="center">
            Already have an account? <Link to="/login">Login here</Link>
          </Typography>
        </Box>
      </Container>
    </>
  );
}

export default Signup;

// worked well including mobile view
// import React, { useState } from 'react';
// import { Button, TextField, Container, Typography, Box, IconButton, CircularProgress, InputAdornment } from '@mui/material';
// import HomeIcon from '@mui/icons-material/Home';
// import { Link, useNavigate } from 'react-router-dom';
// import Header from './Header';
// import axios from 'axios';
// import { toast } from "react-toastify";
// import { Visibility, VisibilityOff } from '@mui/icons-material';

// function Signup() {
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(false);
//   const [formdata, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     email: "",
//     password: "",
//     password2: ""
//   });

//   const [error, setError] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);
//   const apiUrl = process.env.REACT_APP_API_URL;

//   const handleOnChange = (e) => {
//     setFormData({ ...formdata, [e.target.name]: e.target.value });
//   };

//   const { first_name, last_name, email, password, password2 } = formdata;

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!first_name || !last_name || !email || !password || !password2) {
//       setError("All fields are required");
//       toast.error("All fields are required");
//     } else if (password !== password2) {
//       setError("Passwords do not match");
//       toast.error("Passwords do not match");
//     } else {
//       setIsLoading(true);
//       try {
//         const res = await axios.post(`${apiUrl}api/v1/auth/register/`, formdata);
//         setIsLoading(false);
//         const response = res.data;

//         if (res.status === 201) {
//           navigate('/otp-verify');
//           toast.success(response.message || "Registration successful. Please verify your email.");
//         }
//       } catch (error) {
//         setIsLoading(false);
//         console.error("Signup error:", error);

//         let errorMessage = "Signup failed. Please try again.";
//         if (error.response && error.response.data) {
//           if (error.response.status === 400) {
//             const errors = error.response.data;
//             for (const key in errors) {
//               if (errors.hasOwnProperty(key)) {
//                 toast.error(`${key}: ${errors[key][0]}`);
//               }
//             }
//           } else {
//             errorMessage = error.response.data.message || errorMessage;
//             toast.error(errorMessage);
//           }
//         } else {
//           toast.error(errorMessage);
//         }
//       }
//     }
//   };

//   if (isLoading) {
//     return (
//       <div style={{ height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
//         <CircularProgress />
//       </div>
//     );
//   }

//   return (
//     <>
//       <Header />
//       <Box display="flex" justifyContent="center" alignItems="center" mt={{ xs: 2, md: 3 }} mb={{ xs: 1, md: 2 }}>
//         <HomeIcon fontSize="large" />
//         <Typography variant="h4" ml={1} fontSize={{ xs: '1.5rem', md: '2rem' }}>
//           Signup
//         </Typography>
//       </Box>
//       <Container maxWidth="sm">
//         <Box sx={{ p: { xs: 2, md: 3 }, boxShadow: 2, borderRadius: 2 }}>
//           <form onSubmit={handleSubmit}>
//             <Typography color="error" variant="body2" align="center">
//               {error ? error : ""}
//             </Typography>
//             <TextField
//               label="First Name"
//               name="first_name"
//               value={first_name}
//               onChange={handleOnChange}
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               required
//               InputLabelProps={{ style: { fontSize: '1rem' } }}
//             />
//             <TextField
//               label="Last Name"
//               name="last_name"
//               value={last_name}
//               onChange={handleOnChange}
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               required
//               InputLabelProps={{ style: { fontSize: '1rem' } }}
//             />
//             <TextField
//               label="Email"
//               name="email"
//               value={email}
//               onChange={handleOnChange}
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               required
//               InputLabelProps={{ style: { fontSize: '1rem' } }}
//             />
//             <TextField
//               label="Password"
//               name="password"
//               value={password}
//               onChange={handleOnChange}
//               type={showPassword ? "text" : "password"}
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               required
//               InputProps={{
//                 endAdornment: (
//                   <InputAdornment position="end">
//                     <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
//                       {showPassword ? <VisibilityOff /> : <Visibility />}
//                     </IconButton>
//                   </InputAdornment>
//                 ),
//               }}
//               InputLabelProps={{ style: { fontSize: '1rem' } }}
//             />
//             <TextField
//               label="Confirm Password"
//               name="password2"
//               value={password2}
//               onChange={handleOnChange}
//               type={showConfirmPassword ? "text" : "password"}
//               variant="outlined"
//               margin="normal"
//               fullWidth
//               required
//               InputProps={{
//                 endAdornment: (
//                   <InputAdornment position="end">
//                     <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
//                       {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
//                     </IconButton>
//                   </InputAdornment>
//                 ),
//               }}
//               InputLabelProps={{ style: { fontSize: '1rem' } }}
//             />
//             <Button type="submit" variant="contained" color="primary" fullWidth sx={{ mt: 2, py: 1.5, fontSize: { xs: '0.9rem', md: '1rem' } }}>
//               Signup
//             </Button>
//           </form>
//           <Typography mt={2} textAlign="center">
//             Already have an account? <Link to="/login">Login here</Link>
//           </Typography>
//         </Box>
//       </Container>
//     </>
//   );
// }

// export default Signup;

// worked well just that i added mobile responsiveness to the code above
// import React, { useState } from 'react';
// import { Button, TextField, Container, Typography, Box, IconButton, InputAdornment } from '@mui/material';
// import HomeIcon from '@mui/icons-material/Home';
// import { Link, useNavigate } from 'react-router-dom';
// import Header from './Header';
// import axios from 'axios';
// import { toast } from "react-toastify";
// import { Visibility, VisibilityOff } from '@mui/icons-material';

// function Signup() {
//   const navigate = useNavigate();
//   const [formdata, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     email: "",
//     password: "",
//     password2: ""
//   });

//   const [error, setError] = useState("");
//   const [showPassword, setShowPassword] = useState(false);
//   const [showConfirmPassword, setShowConfirmPassword] = useState(false);

//   const handleOnChange = (e) => {
//     setFormData({ ...formdata, [e.target.name]: e.target.value });
//   };

//   const { first_name, last_name, email, password, password2 } = formdata;

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     if (!first_name || !last_name || !email || !password || !password2) {
//       setError("All fields are required");
//       toast.error("All fields are required");
//     } else if (password !== password2) {
//       setError("Passwords do not match");
//       toast.error("Passwords do not match");
//     } else {
//       try {
//         const res = await axios.post("http://localhost:8000/api/v1/auth/register/", formdata);
//         const response = res.data;

//         if (res.status === 201) {
//           navigate('/otp-verify');
//           toast.success(response.message || "Registration successful. Please verify your email.");
//         }
//       } catch (error) {
//         console.error("Signup error:", error);

//         let errorMessage = "Signup failed. Please try again.";
//         if (error.response && error.response.data) {
//           if (error.response.status === 400) {
//             const errors = error.response.data;
//             for (const key in errors) {
//               if (errors.hasOwnProperty(key)) {
//                 toast.error(`${key}: ${errors[key][0]}`);
//               }
//             }
//           } else {
//             errorMessage = error.response.data.message || errorMessage;
//             toast.error(errorMessage);
//           }
//         } else {
//           toast.error(errorMessage);
//         }
//       }
//     }
//   };

//   return (
//     <>
//       <Header />
//       <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={2}>
//         <HomeIcon fontSize="large" />
//         <Typography variant="h4" ml={1}>
//           Signup
//         </Typography>
//       </Box>
//       <Container maxWidth="sm">
//         <form onSubmit={handleSubmit}>
//           <p>{error ? error : ""}</p>
//           <TextField
//             label="First Name"
//             name="first_name"
//             value={first_name}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Last Name"
//             name="last_name"
//             value={last_name}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Email"
//             name="email"
//             value={email}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Password"
//             name="password"
//             value={password}
//             onChange={handleOnChange}
//             type={showPassword ? "text" : "password"}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <IconButton
//                     onClick={() => setShowPassword(!showPassword)}
//                     edge="end"
//                   >
//                     {showPassword ? <VisibilityOff /> : <Visibility />}
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//           />
//           <TextField
//             label="Confirm Password"
//             name="password2"
//             value={password2}
//             onChange={handleOnChange}
//             type={showConfirmPassword ? "text" : "password"}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position="end">
//                   <IconButton
//                     onClick={() => setShowConfirmPassword(!showConfirmPassword)}
//                     edge="end"
//                   >
//                     {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//           />
//           <Button type="submit" variant="contained" color="primary" fullWidth>
//             Signup
//           </Button>
//         </form>
//         <p>
//           Already have an account? <Link to="/login">Login here</Link>
//         </p>
//       </Container>
//     </>
//   );
// }

// export default Signup;

// worked perfectly just needed to  add password visibility to the code above
// import React, {useState} from 'react';
// import { Button, TextField, Container, Typography, Box, IconButton, InputAdornment  } from '@mui/material'; // Use Material-UI components
// import HomeIcon from '@mui/icons-material/Home';
// import { Link, Navigate, useNavigate } from 'react-router-dom';
// import Header from './Header';
// import axios from 'axios';
// import { toast } from "react-toastify";
// import { Visibility, VisibilityOff } from '@mui/icons-material';

// function Signup() {
//   const navigate = useNavigate()
//   const [formdata, setFormData] = useState({
//     first_name: "",
//     last_name: "",
//     email: "",
//     password: "",
//     password2: ""
//   })

//   const [error, setError] = useState("")

//   const handleOnChange = (e) => {
//     setFormData({...formdata, [e.target.name]:e.target.value})
//   }

//   const {first_name, last_name, email, password, password2} = formdata
  
//   const handleSubmit = async (e) => {
//     e.preventDefault();
  
//     if (!first_name || !last_name || !email || !password || !password2) {
//       setError("All fields are required");
//       toast.error("All fields are required");
//     } else if (password !== password2) {
//       setError("Passwords do not match");
//       toast.error("Passwords do not match");
//     } else {
//       try {
//         const res = await axios.post("http://localhost:8000/api/v1/auth/register/", formdata);
//         const response = res.data;
  
//         if (res.status === 201) {
//           navigate('/otp-verify');
//           toast.success(response.message || "Registration successful. Please verify your email.");
//         }
//       } catch (error) {
//         console.error("Signup error:", error);
  
//         // Handle error messages from the backend if available
//         let errorMessage = "Signup failed. Please try again.";
//         if (error.response && error.response.data) {
//           // Check if it's a validation error
//           if (error.response.status === 400) {
//             const errors = error.response.data;
            
//             // Display all error messages (assumes the backend sends an object with field-specific error messages)
//             for (const key in errors) {
//               if (errors.hasOwnProperty(key)) {
//                 toast.error(`${key}: ${errors[key][0]}`);
//               }
//             }
//           } else {
//             errorMessage = error.response.data.message || errorMessage;
//             toast.error(errorMessage);
//           }
//         } else {
//           toast.error(errorMessage);
//         }
//       }
//     }
//   };  

//   // const handleSubmit = async (e) => {
//   //   e.preventDefault();
//   //   if (!first_name || !last_name || !email || !password || !password2) {
//   //     setError("All Fields are required")
//   //   } else {
//   //     const res = await axios.post("http://localhost:8000/api/v1/auth/register/", formdata)
//   //     const response = res.data
//   //     console.log(response)
//   //     if (res.status === 201) {
//   //       navigate('/otp-verify')
//   //       toast.success(response.message)
//   //     }
//   //   }
//   // }

//   return (
//     <>
//       <Header />
//       <Box display="flex" justifyContent="center" alignItems="center" mt={3} mb={2}>
//         <HomeIcon fontSize="large" />
//         <Typography variant="h4" ml={1}>
//           Signup
//         </Typography>
//       </Box>
//       <Container maxWidth="sm">
//         <form onSubmit={handleSubmit}>
//           <p>{error ? error : ""}</p>
//           <TextField
//             label="First Name"
//             name="first_name"
//             value={first_name}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Last Name"
//             name="last_name"
//             value={last_name}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Email"
//             name="email"
//             value={email}
//             onChange={handleOnChange}
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Password"
//             name="password"
//             value={password}
//             onChange={handleOnChange}
//             type="password"
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <TextField
//             label="Confirm Password"
//             name="password2"
//             value={password2}
//             onChange={handleOnChange}
//             type="password"
//             variant="outlined"
//             margin="normal"
//             fullWidth
//             required
//           />
//           <Button type="submit" variant="contained" color="primary" fullWidth>
//             Signup
//           </Button>
//         </form>
//         <p>
//           Already have an account? <Link to="/login">Login here</Link>
//         </p>
//       </Container>
//     </>
//   );
// }

// export default Signup;
